import { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import SnoozeSheet from './SnoozeSheet';
import { canSeeNotificationSuggestions } from '../lib/permissions';
import { DOMAIN_COLORS, DOMAIN_LABELS, chipOf, colorOf, chipsFor } from '../lib/notificationDomain';
import AuditPill from './AuditPill';
import { isAuditEvent, AUDIT_RAIL, AUDIT_TINT } from '../lib/notificationSeverity';

// #314 / #315, 79.12 — its private colour map is gone; see notificationDomain.js.
// This surface already read the `domain` column and was RIGHT to; what it
// lacked was any entry for invoicing, so 28 live rows fell to the grey
// `general` fallback with no badge.


function NotificationCard({ n, onMarkRead, onSnooze, onNavigate, onDismiss }) {
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const touchStartX = useRef(null);
  const DISMISS_THRESHOLD = 60;
  const isAudit = isAuditEvent(n.event_type);
  const borderColor = isAudit ? AUDIT_RAIL : colorOf(n);

  function handleTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
    setSwiping(true);
  }
  function handleTouchMove(e) {
    if (touchStartX.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    if (dx < 0) setSwipeX(Math.max(dx, -80));
  }
  function handleTouchEnd() {
    if (swipeX < -DISMISS_THRESHOLD) {
      onDismiss(n.id);
    } else {
      setSwipeX(0);
    }
    setSwiping(false);
    touchStartX.current = null;
  }

  function handleCardTap() {
    onMarkRead(n.id);
    // #45, Session 79.12 — WAS onNavigate(n.link_to), A STRING, AND THE RECORD
    // ID WENT WITH IT. useNotificationNav() accepts either a page key or the
    // whole notification, and reads link_id ONLY from the object — so a string
    // navigates to the page with nothing selected. The desktop bell has always
    // passed the object; this surface passed the key, so on a phone a
    // notification naming DAL-REQ-00042 opened the Requests inbox showing no
    // request at all. notificationNav.js's own comment calls that "a worse bug"
    // than the one it was written to fix, on the priority surface.
    if (n.link_to) onNavigate(n);
  }

  return (
    <>
      <div style={{ position: 'relative', overflow: 'hidden', borderBottom: '0.5px solid #F0F0F0' }}>
        <div
          style={{
            transform: `translateX(${swipeX}px)`,
            transition: swiping ? 'none' : 'transform 0.2s ease',
            display: 'flex',
            alignItems: 'stretch',
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            style={{ width: 4, flexShrink: 0, background: borderColor, alignSelf: 'stretch' }}
          />
          <div
            style={{ flex: 1, padding: '11px 12px 11px 10px', cursor: 'pointer', background: isAudit ? AUDIT_TINT : 'transparent' }}
            onClick={handleCardTap}
          >
            {isAudit && <div style={{ marginBottom: 4 }}><AuditPill compact /></div>}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a', lineHeight: 1.3, flex: 1 }}>
                {n.title}
              </span>
              <span style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap', flexShrink: 0, paddingTop: 1 }}>
                {formatTime(n.created_at)}
              </span>
            </div>
            {n.message && (
              <div style={{ fontSize: 12, color: '#666', lineHeight: 1.4, marginBottom: 8 }}>
                {n.message}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
              <button
                onClick={e => { e.stopPropagation(); setSnoozeOpen(true); }}
                style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: '#999', fontSize: 16, lineHeight: 1 }}
                aria-label="Snooze"
              >⏱</button>
            </div>
          </div>
        </div>
        <div
          style={{
            position: 'absolute', top: 0, right: 0, bottom: 0,
            width: 80, background: '#E24B4A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: swipeX < -20 ? 1 : 0,
            transition: 'opacity 0.15s',
            pointerEvents: 'none',
          }}
        >
          <span style={{ color: '#fff', fontSize: 11, fontWeight: 500 }}>Dismiss</span>
        </div>
      </div>
      {snoozeOpen && (
        <SnoozeSheet
          onSnooze={(opt) => { onSnooze(n.id, opt); setSnoozeOpen(false); }}
          onClose={() => setSnoozeOpen(false)}
        />
      )}
    </>
  );
}

function formatTime(ts) {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationOverlay({ notifications, onMarkRead, onMarkAllRead, onSnooze, onNavigate, onClose, isAdmin, profile }) {
  const [activeChip, setActiveChip] = useState('all');
  const [dismissed, setDismissed] = useState([]);

  const canSeeSuggestions = canSeeNotificationSuggestions({ isAdmin, profile });
  // One list, shared with the desktop bell. Two hand-kept copies is how the two
  // halves of one bell came to disagree about which chips exist.
  const CHIPS = chipsFor({ canSeeSuggestions })
    .map(key => ({ key, label: key === 'all' ? 'All' : DOMAIN_LABELS[key] }));

  const unread = notifications.filter(n => !n.read && !dismissed.includes(n.id) && (!n.snoozed_until || new Date(n.snoozed_until) <= new Date()));
  const filtered = activeChip === 'all'
    ? unread
    // chipOf, not a bare column comparison: decision 3 puts invoicing under
    // Purchasing, and an equality test here would hide it under every chip.
    : unread.filter(n => chipOf(n) === activeChip);

  function handleDismiss(id) {
    setDismissed(prev => [...prev, id]);
    onMarkRead(id);
  }

  function handleMarkAllRead() {
    onMarkAllRead();
    setDismissed(notifications.map(n => n.id));
  }

  return (
    ReactDOM.createPortal(<>
      <div
        onMouseDown={onClose}
        onTouchEnd={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 998,
          background: 'rgba(0,0,0,0.2)',
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: 56,
          left: 0,
          right: 0,
          width: '100%',
          maxHeight: 'calc(100vh - 56px)',
          background: '#fff',
          borderRadius: 0,
          border: 'none',
          borderTop: '0.5px solid #E8E8E8',
          boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
      >
        <div style={{
          padding: '12px 14px 10px',
          borderBottom: '0.5px solid #F0F0F0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', letterSpacing: '0.02em' }}>
            NOTIFICATIONS
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {unread.length > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{ fontSize: 12, color: '#1D9E75', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >Mark all read</button>
            )}
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 18, lineHeight: 1, padding: 2 }}
              aria-label="Close notifications"
            >×</button>
          </div>
        </div>

        <div style={{
          display: 'flex', gap: 6, padding: '8px 12px',
          overflowX: 'auto', flexShrink: 0,
          borderBottom: '0.5px solid #F0F0F0',
        }}>
          {CHIPS.map(chip => (
            <button
              key={chip.key}
              onClick={e => { e.stopPropagation(); setActiveChip(chip.key); }}
              style={{
                fontSize: 11, fontWeight: 500,
                padding: '4px 10px', borderRadius: 20,
                border: '0.5px solid',
                whiteSpace: 'nowrap', cursor: 'pointer',
                borderColor: activeChip === chip.key ? 'transparent' : '#E0E0E0',
                background: activeChip === chip.key ? (chip.key === 'all' ? '#1a1a1a' : (DOMAIN_COLORS[chip.key] || '#1a1a1a')) : '#fff',
                color: activeChip === chip.key ? (chip.key === 'purchasing' ? '#633806' : '#fff') : '#666',
              }}
            >{chip.label}</button>
          ))}
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '36px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
              <div style={{ fontSize: 13, color: '#999' }}>You're all caught up.</div>
            </div>
          ) : (
            filtered.map(n => (
              <NotificationCard
                key={n.id}
                n={n}
                onMarkRead={onMarkRead}
                onSnooze={onSnooze}
                onNavigate={onNavigate}
                onDismiss={handleDismiss}
              />
            ))
          )}
        </div>

        <div style={{
          borderTop: '0.5px solid #F0F0F0',
          padding: '10px 14px',
          flexShrink: 0,
        }}>
          <button
            onClick={() => { onClose(); setTimeout(() => onNavigate('notifications'), 50); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, color: '#1D9E75', fontWeight: 500, padding: 0,
              width: '100%', textAlign: 'center',
            }}
          >View all notifications →</button>
        </div>
      </div>
    </>, document.body)
  );
}